import { formatLarge } from '@benzinga/utils';
import { ColumnDef } from '@benzinga/table';

import { WatchlistFinancials } from '../../../data';
import { formatCurrency, renderColoredValue } from '../utils';

const renderValue = (value: number | undefined | null, format: 'currency' | 'large' | 'percentage' = 'large') => {
  if (value === undefined || value === null || value === 0) return '-';
  if (format === 'currency') return formatCurrency(value);
  if (format === 'percentage') return `${value.toFixed(2)}%`;
  return formatLarge(value);
};

export const CryptoMetricsColumnsDef: ColumnDef<WatchlistFinancials>[] = [
  {
    field: 'crypto_change_24h',
    headerName: '24h Change',
    sortEnabled: true,
    valueGetter: ({ data }) => data.cryptoData?.price_change_24h ?? null,
    cellRenderer: ({ value }) => renderColoredValue(value, 'currency'),
  },
  {
    field: 'crypto_change_24h_pct',
    headerName: '24h Change %',
    sortEnabled: true,
    valueGetter: ({ data }) => data.cryptoData?.price_change_percentage_24h ?? null,
    cellRenderer: ({ value }) => renderColoredValue(value, 'percentage'),
  },
  {
    field: 'crypto_market_cap',
    headerName: 'Market Cap',
    sortEnabled: true,
    valueGetter: ({ data }) => Number(data.cryptoData?.market_cap) || 0,
    cellRenderer: ({ value }) => <span>{renderValue(value, 'currency')}</span>,
  },
  {
    field: 'crypto_total_volume',
    headerName: '24h Volume',
    sortEnabled: true,
    valueGetter: ({ data }) => data.cryptoData?.total_volume ?? null,
    cellRenderer: ({ value }) => <span>{renderValue(value)}</span>,
  },
  {
    field: 'crypto_24h_range',
    headerName: '24h High/Low',
    sortEnabled: true,
    valueGetter: ({ data }) => data.cryptoData?.low_24h ?? 0,
    cellRenderer: ({ data }) => {
      const high_24h = data.cryptoData?.high_24h;
      const low_24h = data.cryptoData?.low_24h;

      if (high_24h == null || low_24h == null) return '-';

      return (
        <span className="text-xs whitespace-nowrap">
          {formatCurrency(high_24h)} / {formatCurrency(low_24h)}
        </span>
      );
    },
  },
  {
    field: 'crypto_ath',
    headerName: 'All-Time High',
    sortEnabled: true,
    valueGetter: ({ data }) => data.cryptoData?.ath ?? 0,
    cellRenderer: ({ value }) => <span>{renderValue(value, 'currency')}</span>,
  },
  {
    field: 'crypto_ath_change',
    headerName: 'ATH Change %',
    sortEnabled: true,
    valueGetter: ({ data }) => data.cryptoData?.ath_change_percentage ?? null,
    cellRenderer: ({ value }) => renderColoredValue(value, 'percentage'),
  },
  {
    field: 'crypto_circulating_supply',
    headerName: 'Circulating Supply',
    sortEnabled: true,
    valueGetter: ({ data }) => data.cryptoData?.circulating_supply ?? 0,
    cellRenderer: ({ value }) => <span>{renderValue(value)}</span>,
  },
  {
    field: 'crypto_fdv',
    headerName: 'Fully Diluted Val.',
    sortEnabled: true,
    valueGetter: ({ data }) => data.cryptoData?.fully_diluted_valuation ?? 0,
    cellRenderer: ({ value }) => <span>{renderValue(value, 'currency')}</span>,
  },
];
